import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const UpcomingInterviews = ({ interviews }) => {
  const navigate = useNavigate();

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-IN', { 
      weekday: 'short',
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const getDaysUntil = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const diffTime = date - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays < 0) return 'Past';
    return `In ${diffDays} days`;
  };

  if (interviews?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-8 text-center">
        <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="Calendar" size={32} className="text-muted-foreground" />
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-2">No Upcoming Interviews</h3>
        <p className="text-sm text-muted-foreground mb-4">
          You don't have any scheduled interviews at the moment.
        </p>
        <Button
          variant="outline"
          iconName="Search"
          iconPosition="left"
          onClick={() => navigate('/job-listings')}
        >
          Browse Jobs
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {interviews?.map((interview) => (
        <div key={interview?.id} className="bg-card border border-border rounded-lg p-4 sm:p-6 hover:shadow-md transition-smooth">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-shrink-0">
              <div className="w-16 h-16 rounded-lg overflow-hidden bg-muted flex items-center justify-center">
                <Image
                  src={interview?.companyLogo}
                  alt={interview?.companyLogoAlt}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-3">
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-foreground mb-1 truncate">
                    {interview?.jobTitle}
                  </h3>
                  <p className="text-sm text-muted-foreground">{interview?.companyName}</p>
                </div>
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300 rounded-full text-xs font-medium whitespace-nowrap">
                  <Icon name="Clock" size={14} />
                  <span>{getDaysUntil(interview?.date)}</span>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                <div className="flex items-center gap-2 text-sm">
                  <Icon name="Calendar" size={16} className="text-muted-foreground" />
                  <span className="text-foreground">{formatDate(interview?.date)}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Icon name="Clock" size={16} className="text-muted-foreground" />
                  <span className="text-foreground">{interview?.time}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Icon name="Video" size={16} className="text-muted-foreground" />
                  <span className="text-foreground">{interview?.type}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Icon name="User" size={16} className="text-muted-foreground" />
                  <span className="text-foreground">{interview?.interviewer}</span>
                </div>
              </div>

              {interview?.meetingLink && (
                <div className="bg-muted rounded-lg p-3 mb-4">
                  <div className="flex items-center gap-2 text-sm">
                    <Icon name="Link" size={16} className="text-primary" />
                    <a 
                      href={interview?.meetingLink} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary hover:underline truncate"
                    >
                      {interview?.meetingLink}
                    </a>
                  </div>
                </div>
              )}

              {interview?.notes && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-4">
                  <p className="text-sm text-blue-900 dark:text-blue-100">{interview?.notes}</p>
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                <Button
                  variant="default"
                  size="sm"
                  iconName="ExternalLink"
                  iconPosition="left"
                  onClick={() => window.open(interview?.meetingLink, '_blank')}
                  disabled={!interview?.meetingLink}
                >
                  Join Meeting
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="FileText"
                  iconPosition="left"
                  onClick={() => navigate(`/job-details?id=${interview?.jobId}`)}
                >
                  View Job Details
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Download"
                  iconPosition="left"
                >
                  Preparation Guide
                </Button>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default UpcomingInterviews;